/**
 * Admin V3 — Incidents — Search / filter bar
 *
 * Layout (left → right):
 *   [Status filter] [Type filter] [Search input] [Result counter]
 *
 * Status filter: 5 IncidentStatus values (open/acknowledged/in_progress/resolved/closed)
 * Type filter:   7 IncidentType values
 * Both filters are server-side (?status= and ?incident_type= query params).
 * Search input is client-side over loaded page (title + description).
 *
 * NO CSV (no backend endpoint).
 * NO booking_id filter input (could be added later; not in minimal scope).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { incidentStatusMeta, incidentTypeMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

const ALL_SENTINEL = '__all__';

// Phase 2.9 defect remediation — filter values aligned to the ROUTE taxonomy
// (admin_control_tower_v2.py:50-64), which is what the endpoint's Pydantic
// query-param validation actually accepts. Sending values from the SERVICE
// taxonomy (incident_engine.py) returns HTTP 422.
//
// The display dictionary in visualTokens.js retains BOTH taxonomies so that
// stored incidents using the service taxonomy still render with a French
// label even though they cannot be filtered for via the dropdown.
const STATUS_VALUES = ['open', 'in_progress', 'resolved'];
const TYPE_VALUES = [
  'delay', 'no_driver', 'payment_issue', 'customer_complaint',
  'vehicle_issue', 'no_show', 'accident', 'other',
];

const IncidentsListSearchBar = ({
  status,
  onStatusChange,
  incidentType,
  onIncidentTypeChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const statusSelectValue = status || ALL_SENTINEL;
  const typeSelectValue = incidentType || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);
  const handleType = (v) => onIncidentTypeChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('incidents.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Status filter */}
      <Select value={statusSelectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[180px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-incidents-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('incidents.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{incidentStatusMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Type filter */}
      <Select value={typeSelectValue} onValueChange={handleType}>
        <SelectTrigger
          className="h-10 w-[240px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-incidents-type-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('incidents.filter.typeAll', locale)}</SelectItem>
          {TYPE_VALUES.map((v) => (
            <SelectItem key={v} value={v}>{incidentTypeMeta[v]?.label || v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Client-side search */}
      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('incidents.search.placeholder', locale)}
          data-testid="adminv3-incidents-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-incidents-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Result counter */}
      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-incidents-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default IncidentsListSearchBar;
